/**
 * DisputeDaoResolutionCard - DAO Governance, Escrow, and Ruling Execution
 *
 * Implements Figma frames:
 * - Emit Virdict & Close Dispute (#3823:8390)
 * - Dispute Closed, Virdict Emitted (#3820:7313)
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@components/ui';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { Dispute, Project, Milestone } from '@/types';

interface DisputeDaoResolutionCardProps {
  dispute: Dispute;
  project?: Project;
  milestone?: Milestone;
  clientName?: string;
  professionalName?: string;
}

export function DisputeDaoResolutionCard({
  dispute,
  project,
  milestone,
  clientName,
  professionalName,
}: DisputeDaoResolutionCardProps): JSX.Element {
  const { clientRole, workerRole, coordinatorRole } = useDaoConfig();
  const [winner, setWinner] = useState<'client' | 'professional'>('client');
  const [rulingReason, setRulingReason] = useState('');
  const [documentation, setDocumentation] = useState('');
  const [otherLinks, setOtherLinks] = useState('');
  const [showSimModal, setShowSimModal] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showSimModal) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector('button')?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowSimModal(false);
      if (event.key !== 'Tab') return;
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled])'
      );
      const first = controls?.[0],
        last = controls?.[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('keydown', keyboard);
      previous?.focus();
    };
  }, [showSimModal]);

  const isResolved =
    dispute.status === 'RESOLVED' || dispute.status === 'REJECTED';

  const clientDisplayName =
    clientName || project?.client?.name || project?.client?.company || clientRole;
  const professionalDisplayName =
    professionalName ||
    [
      project?.consultant?.name || coordinatorRole,
      milestone?.developer?.name || workerRole,
    ]
      .filter(Boolean)
      .join(' & ');

  const resolvedWinnerName =
    dispute.resolution?.winnerName ||
    (dispute.resolution?.winner === 'client' || dispute.status === 'RESOLVED'
      ? clientDisplayName
      : professionalDisplayName);

  return (
    <div className="space-y-6 rounded-2xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 border-b border-[var(--base-border,#3d3d3d)] pb-4 md:flex-row md:items-center">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-400">
            <i className="ri-scales-3-line text-xl" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">
              DAO Governance & Dispute Resolution
            </h3>
            <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              Decentralized Autonomous Organization (DAO) Arbitration & Escrow
            </p>
          </div>
        </div>

        {!isResolved && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSimModal(true)}
            className="border-purple-500/40 text-xs font-semibold text-purple-300 hover:bg-purple-500/10"
          >
            <i className="ri-auction-line mr-1.5" />
            Emit Virdict & Close Dispute
          </Button>
        )}
      </div>

      {/* If already resolved - Matches Figma frame #3820:7313 */}
      {isResolved && dispute.resolution ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-xl text-emerald-400" />
              <span className="text-base font-bold text-emerald-300">
                Dispute Closed, Virdict Emitted
              </span>
            </div>
            <span className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              {new Date(dispute.resolution.decidedAt).toLocaleString()}
            </span>
          </div>

          {/* Winner Block */}
          <div className="space-y-1.5 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              Winner of the dispute
            </span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-white">
                {resolvedWinnerName}
              </span>
              <span className="rounded-full border border-emerald-500/30 bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                Ruling Winner
              </span>
            </div>
          </div>

          {/* Reason Block */}
          <div className="space-y-1.5 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              Reason behind the decision
            </span>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-200">
              {dispute.resolution.reason}
            </p>
          </div>

          {/* Documentation & Links */}
          {(dispute.resolution.documentation ||
            dispute.resolution.otherLinks) && (
            <div className="space-y-2 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                Attached Documentation & References
              </span>
              <div className="space-y-1">
                {dispute.resolution.documentation && (
                  <div className="flex items-center gap-2 text-xs">
                    <i className="ri-file-text-line text-purple-400" />
                    <span className="text-gray-400">Documentation:</span>
                    <a
                      href={dispute.resolution.documentation}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-[var(--state-brand-active,#36d399)] hover:underline"
                    >
                      {dispute.resolution.documentation}
                    </a>
                  </div>
                )}
                {dispute.resolution.otherLinks && (
                  <div className="flex items-center gap-2 text-xs">
                    <i className="ri-link text-purple-400" />
                    <span className="text-gray-400">Other Links:</span>
                    <a
                      href={dispute.resolution.otherLinks}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-[var(--state-brand-active,#36d399)] hover:underline"
                    >
                      {dispute.resolution.otherLinks}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1 text-xs text-gray-400">
            <span>Escrow settlement finalized by DAO Governance</span>
            <span className="font-mono text-[10px]">
              Judge: {dispute.resolution.judgeAddress || 'DAO Committee'}
            </span>
          </div>
        </div>
      ) : (
        /* Pending resolution info */
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold text-white">
              <i className="ri-safe-2-line text-[var(--state-brand-active,#36d399)]" />
              Escrow Protection
            </h4>
            <p className="text-xs leading-relaxed text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              Funds allocated to this milestone remain locked in escrow. Neither
              client nor developer can unilaterally withdraw during dispute
              proceedings.
            </p>
          </div>

          <div className="space-y-2 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold text-white">
              <i className="ri-scales-line text-amber-400" />
              Arbitration Criteria
            </h4>
            <p className="text-xs leading-relaxed text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              The authority to decide disputes and the settlement rules are
              pending approval. No verdict or unlock action is available yet.
            </p>
          </div>
        </div>
      )}

      {/* Emit Virdict & Close Dispute Modal - Matches Figma frame #3823:8390 */}
      {showSimModal && (
        <div className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            ref={dialogRef}
            aria-modal="true"
            aria-labelledby="verdict-title"
            className="w-full max-w-xl space-y-5 rounded-2xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6 shadow-2xl"
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between">
              <div>
                <h3 id="verdict-title" className="text-xl font-bold text-white">
                  Emit Virdict & Close Dispute
                </h3>
                <p className="mt-0.5 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                  Your Virdict
                </p>
              </div>
              <button
                aria-label="Close verdict modal"
                onClick={() => setShowSimModal(false)}
                className="text-gray-400 transition-colors hover:text-white"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            {/* Winner Selection matching Figma */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-white">
                Who do you rule in favour of? Choose the WINNER of the dispute
              </label>

              <div className="grid grid-cols-2 gap-3">
                {/* The Client Option */}
                <button
                  type="button"
                  onClick={() => setWinner('client')}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    winner === 'client'
                      ? 'border-purple-500 bg-purple-500/15 ring-1 ring-purple-400'
                      : 'border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] hover:border-gray-500'
                  }`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <input
                      type="radio"
                      checked={winner === 'client'}
                      onChange={() => setWinner('client')}
                      className="h-4 w-4 accent-purple-500"
                    />
                    <span className="text-xs font-semibold text-gray-400">
                      The Client
                    </span>
                  </div>
                  <p className="pl-6 text-sm font-bold text-white">
                    {clientDisplayName}
                  </p>
                </button>

                {/* The Professional Option */}
                <button
                  type="button"
                  onClick={() => setWinner('professional')}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    winner === 'professional'
                      ? 'border-purple-500 bg-purple-500/15 ring-1 ring-purple-400'
                      : 'border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] hover:border-gray-500'
                  }`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <input
                      type="radio"
                      checked={winner === 'professional'}
                      onChange={() => setWinner('professional')}
                      className="h-4 w-4 accent-purple-500"
                    />
                    <span className="text-xs font-semibold text-gray-400">
                      The Professional
                    </span>
                  </div>
                  <p className="truncate pl-6 text-sm font-bold text-white">
                    {professionalDisplayName}
                  </p>
                </button>
              </div>
            </div>

            {/* Field 1: Reason behind the decision (Required) */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-white">
                Reason behind the decision (Required){' '}
                <span className="text-rose-400">*</span>
              </label>
              <textarea
                aria-label="Reason behind the decision"
                value={rulingReason}
                onChange={(e) => setRulingReason(e.target.value)}
                placeholder="Explain the rationale behind your decision"
                rows={4}
                required
                className="w-full resize-none rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>

            {/* Field 2: Documentation (Optional) */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-white">
                Documentation (Optional)
              </label>
              <input
                aria-label="Verdict documentation"
                type="text"
                value={documentation}
                onChange={(e) => setDocumentation(e.target.value)}
                placeholder="Provide a link to documentation if necessary"
                className="w-full rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>

            {/* Field 3: Other Links (Optional) */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-white">
                Other Links (Optional)
              </label>
              <input
                aria-label="Verdict other links"
                type="text"
                value={otherLinks}
                onChange={(e) => setOtherLinks(e.target.value)}
                placeholder="Add links to supporting documents"
                className="w-full rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>

            <p role="status" className="text-xs text-gray-400">
              Formal dispute resolution is not connected. This form cannot issue
              a verdict or release funds.
            </p>
            {/* The legacy form remains available; resolution is outside this phase. */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={() => setShowSimModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled
                className="flex-1 !border-purple-600 !bg-purple-600 font-semibold hover:!bg-purple-700"
              >
                Emit Virdict & Close Dispute
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
